import React, { useRef, useState } from 'react';
import Router from 'next/router';
import { confirmSignIn, resendSignUpCode, resetPassword } from 'aws-amplify/auth';
import { HStack, Input, Link, Spinner, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { toaster, Toaster } from '@/components/ui/toaster';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';

import { AuthStatus, useAuthDispatch, useAuthState } from '../../contexts/AuthContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useColorModeValue } from '../ui/color-mode';
import { Field } from '../ui/field';

type VerifyCodeFormProps = {
  type?:
    | 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION'
    | 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP'
    | 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
    | 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
    | 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
    | 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
    | 'CONFIRM_SIGN_UP';
  user?: {
    username: string;
    password: string;
  };
  callback?: () => void;
};

const CODE_LENGTH = 6;
const createEmptyCode = () => Array.from({ length: CODE_LENGTH }, () => '');
const sanitiseCode = (value: string) => value.replace(/\D/g, '').slice(0, CODE_LENGTH);

const VerifyCodeForm = ({ type, user, callback }: VerifyCodeFormProps) => {
  const { logEvent } = useAnalytics();
  const { signUpConfirm, dispatch } = useAuthDispatch();
  const { sessionStatus } = useAuthState();
  const codeInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const codeRef = useRef(createEmptyCode());
  const [code, setCode] = useState(createEmptyCode);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('auth');

  const focusCodeInput = (index: number) => {
    window.setTimeout(() => {
      codeInputRefs.current[index]?.focus({ preventScroll: true });
    }, 0);
  };

  const setCodeValue = (value: string[]) => {
    codeRef.current = value;
    setCode(value);
  };

  const resetCodeInput = () => {
    setCodeValue(createEmptyCode());
    setLoading(false);
    focusCodeInput(0);
  };

  const handleResendCode = async () => {
    setLoading(true);

    try {
      // Continue signup
      if (type === 'CONFIRM_SIGN_UP') {
        await resendSignUpCode({ username: user?.username || '' });
      }

      // Everything else
      if (type !== 'CONFIRM_SIGN_UP') {
        await resetPassword({ username: user?.username || '' });
      }

      setLoading(false);
      toaster.create({
        title: t('verify.resent'),
        description: t('verify.resentTo', { email: user?.username || '' }),
        type: 'success'
      });
    } catch (err: any) {
      setLoading(false);
      setError(err.message || t('verify.limitReached'));
    }
  };

  async function handleCodeComplete(valueAsString: string) {
    if (loading) {
      return;
    }

    setError('');
    setLoading(true);

    // Continue signup
    if (type === 'CONFIRM_SIGN_UP') {
      // TODO: Add option throwError?: boolean
      const resp = await signUpConfirm({
        code: valueAsString,
        email: user?.username
      }).catch((err) => {
        console.error({ action: 'onConfirmCode:Auth.confirmSignUp:error', error: err });
        resetCodeInput();
        // TODO: Override errors in AuthContext
        // setError('Invalid code. Please use the latest email confirmation code and try again.');
      });

      // Let parent component know to Redirect to sign-in form
      // @ts-ignore
      if (resp?.nextStep?.signUpStep === 'DONE' && callback) {
        callback();
      }

      logEvent({
        action: 'signed_up_confirmed',
        category: 'Sign up confirmed'
      });

      return;
    }

    // MFA
    if (type === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      // @ts-ignore
      const resp = await confirmSignIn({ challengeResponse: valueAsString }).catch((err) => {
        console.error({ action: 'onConfirmCode:Auth.confirmSignInTotp:error', error: err });
        dispatch({ type: 'signInTotpError', error: err });
        resetCodeInput();
        // TODO: Override errors in AuthContext
        // setError('Invalid code. Please use the latest email confirmation code and try again.');
      });

      // Let parent component know to Redirect to sign-in form
      // @ts-ignore
      if (resp?.nextStep?.signUpStep === 'DONE' && callback) {
        callback();
      }

      logEvent({
        action: 'signed_in_totp',
        category: 'Signed in with TOTP'
      });

      return;
    }

    // Everything else
    const codeConfirmed = await confirmSignIn({ challengeResponse: valueAsString }).catch((err) => {
      console.error({ action: 'onConfirmCode:Auth.confirmSignIn:error', error: err });
      resetCodeInput();
      setError(t('verify.invalidCode'));
    });

    // returned if not verified
    if (!codeConfirmed) {
      return;
    }

    // Route to account on successful login
    await Router.push('/dashboard/').catch((e) => {
      console.error({ action: 'onEmailVerified:route:error', error: e });
      setError(t('verify.loadError'));
    });
  }

  const updateCodeInput = (index: number, value: string) => {
    const digits = sanitiseCode(value);
    const nextCode = [...codeRef.current];

    if (!digits) {
      nextCode[index] = '';
      setCodeValue(nextCode);
      return;
    }

    digits.split('').forEach((digit, offset) => {
      const targetIndex = index + offset;
      if (targetIndex < CODE_LENGTH) {
        nextCode[targetIndex] = digit;
      }
    });

    setCodeValue(nextCode);

    if (nextCode.every(Boolean)) {
      void handleCodeComplete(nextCode.join(''));
      return;
    }

    focusCodeInput(Math.min(index + digits.length, CODE_LENGTH - 1));
  };

  const handleCodeChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    updateCodeInput(index, event.currentTarget.value);
  };

  const handleCodePaste = (index: number, event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    updateCodeInput(index, event.clipboardData.getData('text'));
  };

  const handleCodeKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusCodeInput(Math.max(index - 1, 0));
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusCodeInput(Math.min(index + 1, CODE_LENGTH - 1));
      return;
    }

    if (event.key !== 'Backspace') {
      return;
    }

    event.preventDefault();
    const nextCode = [...codeRef.current];

    if (nextCode[index]) {
      nextCode[index] = '';
      setCodeValue(nextCode);
      return;
    }

    const previousIndex = Math.max(index - 1, 0);
    nextCode[previousIndex] = '';
    setCodeValue(nextCode);
    focusCodeInput(previousIndex);
  };

  const handleTotpCancel = () => {
    dispatch({ type: 'anonymousSession' });
  };

  return (
    <VStack gap={6} alignItems='start'>
      {/* Errors */}
      {error.length > 0 && (
        <Alert status='error' colorPalette='brand' mb={6}>
          {error}
        </Alert>
      )}

      {/* Code */}
      <Field
        label={type !== 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ? t('verify.emailCode') : t('verify.mfaCode')}
        invalid={!!error}
      >
        <HStack mb={6} gap={2} mt={6}>
          {code.map((digit, index) => (
            <Input
              key={index}
              ref={(node) => {
                codeInputRefs.current[index] = node;
              }}
              data-index={index}
              aria-label={`${t('verify.mfaCode')} ${index + 1}`}
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              autoFocus={index === 0}
              disabled={loading}
              h={10}
              inputMode='numeric'
              maxLength={CODE_LENGTH}
              onChange={(event) => handleCodeChange(index, event)}
              onFocus={(event) => event.currentTarget.select()}
              onKeyDown={(event) => handleCodeKeyDown(index, event)}
              onPaste={(event) => handleCodePaste(index, event)}
              pattern='[0-9]*'
              textAlign='center'
              type='tel'
              value={digit}
              w={10}
            />
          ))}
          <Spinner
            display={loading || sessionStatus === AuthStatus.PENDING_SIGN_UP_CONFIRM ? 'block' : 'none'}
            color='brand.300'
          />
        </HStack>
      </Field>

      {type !== 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' && (
        <>
          <Text fontSize='xs' color={useColorModeValue('gray.500', 'gray.500')}>
            {t('verify.codeSentMessage')}
          </Text>
          <Text fontSize='xs' color={useColorModeValue('gray.500', 'gray.500')}>
            {}
            <Link onClick={handleResendCode}>{t('verify.didNotReceive')}</Link>
          </Text>
        </>
      )}

      {type === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' && (
        <Button variant='ghost' onClick={handleTotpCancel}>
          {t('verify.cancel')}
        </Button>
      )}
      <Toaster />
    </VStack>
  );
};

export default VerifyCodeForm;
